import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  Grid,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Chip,
  Avatar,
  Rating,
  Snackbar,
  Alert,
  Dialog,
  IconButton,
  AppBar,
  Toolbar,
  Slide,
  Fade,
  Badge,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';

// Icons
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PhoneIcon from '@mui/icons-material/Phone';
import BuildIcon from '@mui/icons-material/Build';
import PlumbingIcon from '@mui/icons-material/Plumbing';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import FoundationIcon from '@mui/icons-material/Foundation';
import DoorSlidingIcon from '@mui/icons-material/DoorSliding';
import SecurityIcon from '@mui/icons-material/Security';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VerifiedIcon from '@mui/icons-material/Verified';
import HandymanIcon from '@mui/icons-material/Handyman';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import StarIcon from '@mui/icons-material/Star';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MenuIcon from '@mui/icons-material/Menu';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import GroupsIcon from '@mui/icons-material/Groups';
import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/Send';

const PHONE_NUMBER = '0537750445';
const WHATSAPP_NUMBER = '972537750445';

// צבעים חדשים - כתום/אדום שמושך יותר את העין
const colors = {
  primary: '#E65100', // כתום כהה
  secondary: '#FF6D00', // כתום בהיר
  accent: '#FF3D00', // אדום-כתום
  dark: '#1A1A2E', // כהה
  light: '#FFF8F0', // קרם בהיר
  white: '#FFFFFF',
  gray: '#64748B',
  lightGray: '#E2E8F0',
  gold: '#FFB300', // זהב
};

const DanielLandingPage = () => {
  const [formData, setFormData] = useState({ name: '', phone: '', message: '' });
  const [showSuccess, setShowSuccess] = useState(false);
  const [activeService, setActiveService] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [liveViewers, setLiveViewers] = useState(12);
  const [spotsLeft, setSpotsLeft] = useState(10);
  
  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({ name: '', text: '', rating: 5, service: 'שיפוצים' });
  const [reviewSuccess, setReviewSuccess] = useState(false);

  // Load reviews from localStorage
  useEffect(() => {
    const savedReviews = localStorage.getItem('danielReviews');
    if (savedReviews) {
      setReviews(JSON.parse(savedReviews));
    }
  }, []);

  // Popup timer - מציג אחרי 3 שניות
  useEffect(() => {
    const popupTimer = setTimeout(() => {
      const hasSeenPopup = sessionStorage.getItem('seenPopup');
      if (!hasSeenPopup) {
        setShowPopup(true);
      }
    }, 3000);
    return () => clearTimeout(popupTimer);
  }, []);

  // Navbar scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fake live viewers
  useEffect(() => {
    const interval = setInterval(() => {
      setLiveViewers(prev => {
        const change = Math.random() > 0.5 ? 1 : -1;
        return Math.max(8, Math.min(25, prev + change));
      });
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const closePopup = () => {
    setShowPopup(false);
    sessionStorage.setItem('seenPopup', 'true');
  };

  const handleWhatsApp = (msg = 'היי! אני מעוניין לקבל הצעת מחיר 🔧') => {
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const handleCall = () => {
    window.location.href = `tel:${PHONE_NUMBER}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const msg = `היי! שמי ${formData.name}.\nטלפון: ${formData.phone}\n${formData.message || 'אשמח לקבל הצעת מחיר'}`;
    handleWhatsApp(msg);
    setShowSuccess(true);
    setFormData({ name: '', phone: '', message: '' });
  };

  const scrollToForm = () => {
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Submit review to localStorage
  const handleReviewSubmit = (e) => {
    e.preventDefault();
    const newReview = {
      ...reviewForm,
      id: Date.now(),
      date: new Date().toLocaleDateString('he-IL'),
    };
    const updatedReviews = [newReview, ...reviews];
    setReviews(updatedReviews);
    localStorage.setItem('danielReviews', JSON.stringify(updatedReviews));
    setReviewForm({ name: '', text: '', rating: 5, service: 'שיפוצים' });
    setShowReviewForm(false);
    setReviewSuccess(true);
    setTimeout(() => setReviewSuccess(false), 3000);
  };

  // Gallery categories - each with a cover image and all images for lightbox
  const galleryCategories = [
    {
      name: 'שיפוצים',
      cover: '/images/renovations/general1.jpeg',
      desc: 'שיפוץ דירות ומבנים',
      color: colors.primary,
      images: [
        { img: '/images/renovations/general1.jpeg', title: 'עבודות שיפוצים' },
        { img: '/images/renovations/general2.jpeg', title: 'שיפוצים ואינסטלציה' },
        { img: '/images/renovations/reno1.jpeg', title: 'שיפוץ דירה' },
        { img: '/images/renovations/reno2.jpeg', title: 'שיפוץ דירה - שלב ביניים' },
        { img: '/images/renovations/reno3.jpeg', title: 'שיפוץ דירה - גמר' },
        { img: '/images/renovations/reno4.jpeg', title: 'שיפוץ דירה מלא' },
        { img: '/images/renovations/reno5.jpeg', title: 'שיפוץ דירה - תוצאה סופית' },
        { img: '/images/renovations/reno6.jpeg', title: 'שיפוץ כללי' },
      ]
    },
    {
      name: 'מטבח',
      cover: '/images/renovations/kitchen.jpeg',
      desc: 'שיפוץ והתקנת מטבחים',
      color: '#8BC34A',
      images: [
        { img: '/images/renovations/kitchen.jpeg', title: 'שיפוץ מטבח' },
      ]
    },
    {
      name: 'אמבטיות ומקלחות',
      cover: '/images/renovations/bathroom.jpeg',
      desc: 'שיפוץ חדרי רחצה',
      color: '#00BCD4',
      images: [
        { img: '/images/renovations/bathroom.jpeg', title: 'שיפוץ מקלחת' },
        { img: '/images/renovations/bath.jpeg', title: 'עבודת אמבטיה' },
      ]
    },
    {
      name: 'מזגנים',
      cover: '/images/ac/install1.jpeg',
      desc: 'התקנה וניקוי מזגנים',
      color: '#03A9F4',
      images: [
        { img: '/images/ac/install1.jpeg', title: 'התקנת מזגן' },
        { img: '/images/ac/install2.jpeg', title: 'התקנת מזגן - יחידה חיצונית' },
        { img: '/images/ac/clean1.jpeg', title: 'ניקוי מזגן' },
        { img: '/images/ac/clean2.jpeg', title: 'ניקוי מזגן - מסננים' },
        { img: '/images/ac/clean3.jpeg', title: 'ניקוי מזגן - תחזוקה' },
        { img: '/images/ac/clean4.jpeg', title: 'ניקוי מזגן מקצועי' },
      ]
    },
    {
      name: 'דודי שמש',
      cover: '/images/solar/solar1.jpeg',
      desc: 'התקנה והחלפת דודי שמש',
      color: '#FF9800',
      images: [
        { img: '/images/solar/solar1.jpeg', title: 'התקנת דוד שמש' },
        { img: '/images/solar/solar2.jpeg', title: 'עבודה על הגג' },
        { img: '/images/solar/solar3.jpeg', title: 'החלפת דוד שמש' },
      ]
    },
    {
      name: 'סורגים',
      cover: '/images/bars/bars1.jpeg',
      desc: 'התקנת סורגים לבית ולעסק',
      color: '#607D8B',
      images: [
        { img: '/images/bars/bars1.jpeg', title: 'התקנת סורגים לחלון' },
        { img: '/images/bars/bars2.jpeg', title: 'סורגים מעוצבים' },
        { img: '/images/bars/bars3.jpeg', title: 'סורגים עם נעילה' },
        { img: '/images/bars/bars4.jpeg', title: 'התקנת סורגים - גמר' },
      ]
    },
    {
      name: 'אינסטלציה',
      cover: '/images/plumbing/plumbing1.jpeg',
      desc: 'עבודות אינסטלציה 24/6',
      color: '#2196F3',
      images: [
        { img: '/images/plumbing/plumbing1.jpeg', title: 'עבודות אינסטלציה' },
      ]
    },
  ];

  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(null);

  const openLightbox = (categoryIndex, imageIndex = 0) => {
    setSelectedCategoryIndex(categoryIndex);
    setLightboxIndex(imageIndex);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setSelectedCategoryIndex(null);
  };

  const nextImage = () => {
    if (selectedCategoryIndex !== null) {
      const images = galleryCategories[selectedCategoryIndex].images;
      setLightboxIndex((prev) => (prev + 1) % images.length);
    }
  };

  const prevImage = () => {
    if (selectedCategoryIndex !== null) {
      const images = galleryCategories[selectedCategoryIndex].images;
      setLightboxIndex((prev) => (prev - 1 + images.length) % images.length);
    }
  };

  const getCurrentImage = () => {
    if (selectedCategoryIndex !== null && galleryCategories[selectedCategoryIndex]) {
      return galleryCategories[selectedCategoryIndex].images[lightboxIndex];
    }
    return null;
  };

  const getCurrentCategory = () => {
    if (selectedCategoryIndex !== null) {
      return galleryCategories[selectedCategoryIndex];
    }
    return null;
  };

  // Services data
  const services = [
    { icon: <PlumbingIcon sx={{ fontSize: 40 }} />, title: 'אינסטלציה', desc: 'תיקון נזילות, החלפת צנרת והתקנות. זמינים 24/6', highlight: true },
    { icon: <BuildIcon sx={{ fontSize: 40 }} />, title: 'שיפוצים', desc: 'שיפוץ מלא של דירות, משרדים ומבנים מסחריים' },
    { icon: <AcUnitIcon sx={{ fontSize: 40 }} />, title: 'מזגנים', desc: 'התקנה, ניקוי עמוק ותחזוקה שוטפת' },
    { icon: <WbSunnyIcon sx={{ fontSize: 40 }} />, title: 'דודי שמש', desc: 'התקנה, החלפה ותיקון - מתקין מוסמך' },
    { icon: <WaterDropIcon sx={{ fontSize: 40 }} />, title: 'איתור נזילות', desc: 'איתור נזילות סמויות בציוד מתקדם' },
    { icon: <FoundationIcon sx={{ fontSize: 40 }} />, title: 'בנייה קלה', desc: 'קירות גבס, מחיצות ותקרות אקוסטיות' },
    { icon: <DoorSlidingIcon sx={{ fontSize: 40 }} />, title: 'דלתות', desc: 'התקנת דלתות פנים וחוץ - מתקין מוסמך' },
    { icon: <SecurityIcon sx={{ fontSize: 40 }} />, title: 'סורגים', desc: 'התקנת סורגים ביטחוניים לבית ולעסק' },
  ];

  // Advantages
  const advantages = [
    { icon: <VerifiedIcon />, title: '8 שנות ניסיון', desc: 'מקצועיות ואיכות מוכחת בשטח' },
    { icon: <AccessTimeIcon />, title: 'זמינות 24/6', desc: 'כולל לילות, חוץ משבת' },
    { icon: <LocalOfferIcon />, title: '15% הנחה', desc: 'מבצע מיוחד ללקוחות חדשים' },
    { icon: <HandymanIcon />, title: 'עמידה בזמנים', desc: 'מסיימים בדיוק כשסיכמנו' },
    { icon: <SupportAgentIcon />, title: 'שירות אישי', desc: 'ליווי צמוד מההתחלה ועד הסוף' },
    { icon: <CheckCircleIcon />, title: 'אחריות מלאה', desc: 'אחריות על כל העבודות שלנו' },
  ];

  // Testimonials
  const testimonials = [
    { name: 'יוסי כהן', location: 'תל אביב', text: 'דניאל הגיע תוך שעה לתקן נזילה דחופה. עבודה מקצועית, מחיר הוגן, ממליץ בחום!', rating: 5, service: 'אינסטלציה' },
    { name: 'רונית לוי', location: 'רמת גן', text: 'שיפצנו את כל הדירה. דניאל עמד בכל ההבטחות - בזמנים, בתקציב ובאיכות. תודה רבה!', rating: 5, service: 'שיפוצים' },
    { name: 'אבי מזרחי', location: 'חולון', text: 'התקנת 3 מזגנים בבית החדש. עבודה נקייה ומסודרת. כבר חצי שנה והכל עובד מצוין.', rating: 5, service: 'מזגנים' },
    { name: 'שרה גולדברג', location: 'בת ים', text: 'הדוד שמש הפסיק לעבוד. דניאל בא, הסביר את הבעיה והחליף אותו באותו יום. שירות מעולה!', rating: 5, service: 'דודי שמש' },
  ];

  // FAQ
  const faqItems = [
    { q: 'באילו אזורים אתם עובדים?', a: 'אנחנו נותנים שירות באזור המרכז, השרון, השפלה והדרום. צרו קשר ונבדוק זמינות באזור שלכם.' },
    { q: 'יש שירות חירום?', a: 'כן! שירותי אינסטלציה זמינים 24/6 (ראשון עד שישי), כולל בשעות הלילה. פשוט התקשרו ונגיע.' },
    { q: 'כמה עולה לקבל הצעת מחיר?', a: 'הצעת מחיר היא חינם וללא התחייבות. אנחנו מגיעים, בודקים ונותנים מחיר מדויק במקום.' },
    { q: 'יש אחריות על העבודה?', a: 'בהחלט! כל העבודות שלנו מגיעות עם אחריות מלאה. תקופת האחריות משתנה לפי סוג העבודה.' },
    { q: 'אתם מביאים את החומרים?', a: 'כן, אנחנו דואגים להכל - חומרים, כלים וציוד מקצועי. אתם רק צריכים להגיד מה צריך לעשות.' },
    { q: 'כמה זמן לוקחת עבודה?', a: 'תיקון אינסטלציה פשוט לוקח בין שעה לשעתיים. שיפוץ דירה - נקבע לפי היקף העבודה בפגישה.' },
  ];

  return (
    <Box sx={{ background: colors.light, minHeight: '100vh', direction: 'rtl' }}>
      
      {/* Navbar */}
      <AppBar 
        position="fixed" 
        elevation={scrolled ? 4 : 0}
        sx={{ 
          background: scrolled ? 'rgba(26, 26, 46, 0.95)' : 'transparent',
          backdropFilter: scrolled ? 'blur(10px)' : 'none',
          transition: 'all 0.3s ease',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <img src="/logo.jpeg" alt="דניאל" style={{ height: 40, borderRadius: 4 }} />
            <Typography sx={{ fontWeight: 700, color: '#fff', display: { xs: 'none', sm: 'block' } }}>
              דניאל אחזקות
            </Typography>
          </Box>
          
          <Stack direction="row" spacing={{ xs: 1, md: 2 }} alignItems="center">
            {/* Live viewers badge */}
            <Chip
              icon={<GroupsIcon sx={{ color: '#4ADE80 !important', fontSize: 16 }} />}
              label={`${liveViewers} צופים עכשיו`}
              size="small"
              sx={{ 
                background: 'rgba(74, 222, 128, 0.15)',
                color: '#4ADE80',
                fontWeight: 600,
                fontSize: '0.75rem',
                display: { xs: 'none', md: 'flex' },
                '& .MuiChip-icon': { mr: 0.5 },
              }}
            />
            
            <Button
              variant="contained"
              size="small"
              startIcon={<PhoneIcon />}
              onClick={handleCall}
              sx={{
                background: colors.secondary,
                fontWeight: 700,
                borderRadius: 2,
                gap: 1,
                display: { xs: 'none', sm: 'flex' },
                '&:hover': { background: colors.accent },
              }}
            >
              {PHONE_NUMBER}
            </Button>
            
            <Button
              variant="contained"
              size="small"
              onClick={() => handleWhatsApp()}
              sx={{
                background: '#25D366',
                minWidth: 'auto',
                p: 1,
                borderRadius: 2,
                '&:hover': { background: '#20BD5A' },
              }}
            >
              <WhatsAppIcon />
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Special Offer Popup */}
      <Dialog
        open={showPopup}
        onClose={closePopup}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
            m: 2,
          }
        }}
      >
        <Box sx={{ position: 'relative' }}>
          {/* Close button */}
          <IconButton
            onClick={closePopup}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 10,
              background: 'rgba(255,255,255,0.9)',
              '&:hover': { background: '#fff' },
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Header */}
          <Box sx={{ 
            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`,
            p: 3,
            textAlign: 'center',
          }}>
            <motion.div
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <WorkspacePremiumIcon sx={{ fontSize: 60, color: colors.gold }} />
            </motion.div>
            <Typography variant="h4" sx={{ color: '#fff', fontWeight: 800, mt: 1 }}>
              🎉 מבצע מיוחד!
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: colors.dark, mb: 2 }}>
              15% הנחה על כל העבודות
            </Typography>
            
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              gap: 2, 
              mb: 3,
              flexWrap: 'wrap',
            }}>
              <Chip 
                icon={<ElectricBoltIcon />} 
                label={`נותרו רק ${spotsLeft} מקומות!`}
                color="error"
                sx={{ fontWeight: 700, py: 2.5 }}
              />
            </Box>

            <Typography sx={{ color: colors.gray, mb: 3 }}>
              המבצע תקף ללקוחות חדשים בלבד.<br/>
              צור קשר עכשיו ותקבל הצעת מחיר מיוחדת!
            </Typography>

            <Stack spacing={2}>
              <Button
                variant="contained"
                size="large"
                fullWidth
                startIcon={<WhatsAppIcon />}
                onClick={() => { handleWhatsApp('היי! ראיתי את המבצע של 15% הנחה ואשמח לקבל הצעת מחיר 🎉'); closePopup(); }}
                sx={{
                  background: '#25D366',
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  borderRadius: 3,
                  gap: 1.5,
                  '&:hover': { background: '#20BD5A' },
                }}
              >
                שלח הודעה בוואטסאפ
              </Button>
              
              <Button
                variant="outlined"
                onClick={closePopup}
                sx={{ color: colors.gray, borderColor: colors.lightGray }}
              >
                אולי אחר כך
              </Button>
            </Stack>
          </Box>
        </Box>
      </Dialog>

      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${colors.dark} 0%, ${colors.primary} 100%)`,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          pt: { xs: 10, md: 8 }, // מקום ל-navbar
        }}
      >
        {/* Background Pattern */}
        <Box sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `radial-gradient(circle at 20% 50%, ${colors.secondary}22 0%, transparent 50%),
                           radial-gradient(circle at 80% 80%, ${colors.accent}15 0%, transparent 40%)`,
        }} />

        {/* Floating Elements */}
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            style={{
              position: 'absolute',
              width: 60 + i * 20,
              height: 60 + i * 20,
              borderRadius: '50%',
              border: `2px solid ${colors.secondary}33`,
              left: `${10 + i * 15}%`,
              top: `${20 + (i % 3) * 25}%`,
            }}
            animate={{
              y: [0, -20, 0],
              scale: [1, 1.05, 1],
            }}
            transition={{
              duration: 4 + i,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        ))}

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>
          <Grid container spacing={{ xs: 2, md: 4 }} alignItems="center">
            <Grid item xs={12} md={7}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                {/* Logo */}
                <Box sx={{ mb: { xs: 2, md: 4 }, textAlign: { xs: 'center', md: 'right' } }}>
                  <img 
                    src="/logo.jpeg" 
                    alt="דניאל אחזקות" 
                    style={{ maxWidth: '160px', borderRadius: 8, width: '100%', maxHeight: '80px', objectFit: 'contain' }}
                  />
                </Box>

                {/* Badge */}
                <Box sx={{ textAlign: { xs: 'center', md: 'right' } }}>
                  <Chip
                    icon={<LocalOfferIcon sx={{ color: '#fff !important', fontSize: { xs: 16, md: 20 } }} />}
                    label="15% הנחה ללקוחות חדשים!"
                    sx={{
                      mb: { xs: 2, md: 3 },
                      py: { xs: 1.5, md: 2.5 },
                      px: { xs: 0.5, md: 1 },
                      background: colors.secondary,
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: { xs: '0.8rem', md: '1rem' },
                    }}
                  />
                </Box>

                <Typography
                  variant="h1"
                  sx={{
                    color: '#fff',
                    fontWeight: 900,
                    fontSize: { xs: '1.8rem', sm: '2.2rem', md: '3.5rem' },
                    lineHeight: 1.2,
                    mb: { xs: 1.5, md: 2 },
                    textAlign: { xs: 'center', md: 'right' },
                    wordBreak: 'keep-all',
                  }}
                >
                  שיפוצים ואחזקה
                  <br />
                  <Box component="span" sx={{ color: colors.secondary }}>
                    ברמה אחרת
                  </Box>
                </Typography>

                <Typography
                  sx={{
                    color: 'rgba(255,255,255,0.8)',
                    fontSize: { xs: '0.95rem', md: '1.3rem' },
                    mb: { xs: 3, md: 4 },
                    maxWidth: 500,
                    textAlign: { xs: 'center', md: 'right' },
                    mx: { xs: 'auto', md: 0 },
                    lineHeight: 1.6,
                  }}
                >
                  8 שנות ניסיון • זמינות 24/6 • עמידה בזמנים
                  <br />
                  כל עבודות הבית תחת קורת גג אחת - מקצועי ואמין
                </Typography>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ justifyContent: { xs: 'center', md: 'flex-start' } }}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<WhatsAppIcon />}
                    onClick={() => handleWhatsApp()}
                    sx={{
                      background: '#25D366',
                      color: '#fff',
                      py: { xs: 1.5, md: 2 },
                      px: { xs: 3, md: 4 },
                      fontSize: { xs: '0.95rem', md: '1.1rem' },
                      fontWeight: 700,
                      borderRadius: 3,
                      width: { xs: '100%', sm: 'auto' },
                      gap: 1.5, // מרחק בין אייקון לטקסט
                      '&:hover': { background: '#20BD5A', transform: 'scale(1.02)' },
                    }}
                  >
                    שלח הודעה בוואטסאפ
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<PhoneIcon />}
                    onClick={handleCall}
                    sx={{
                      borderColor: '#fff',
                      color: '#fff',
                      py: { xs: 1.5, md: 2 },
                      px: { xs: 3, md: 4 },
                      fontSize: { xs: '0.95rem', md: '1.1rem' },
                      fontWeight: 700,
                      borderRadius: 3,
                      borderWidth: 2,
                      gap: 1.5, // מרחק בין אייקון לטקסט
                      width: { xs: '100%', sm: 'auto' },
                      '&:hover': { borderColor: colors.secondary, color: colors.secondary, borderWidth: 2 },
                    }}
                  >
                    {PHONE_NUMBER}
                  </Button>
                </Stack>

                {/* Trust badges */}
                <Stack 
                  direction="row" 
                  spacing={{ xs: 2, md: 4 }} 
                  sx={{ mt: { xs: 3, md: 5 }, justifyContent: { xs: 'center', md: 'flex-start' }, flexWrap: 'wrap', gap: { xs: 1, md: 0 } }}
                >
                  {[
                    { icon: '🔧', text: '8 שנות ניסיון' },
                    { icon: '⏰', text: 'זמינות 24/6' },
                    { icon: '✅', text: 'אחריות מלאה' },
                  ].map((item, i) => (
                    <Box key={i} sx={{ textAlign: 'center', minWidth: { xs: 80, md: 'auto' } }}>
                      <Typography sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' }, mb: 0.5 }}>{item.icon}</Typography>
                      <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: { xs: '0.75rem', md: '0.85rem' } }}>{item.text}</Typography>
                    </Box>
                  ))}
                </Stack>
              </motion.div>
            </Grid>

            {/* Quick Contact Card */}
            <Grid item xs={12} md={5}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                <Card
                  sx={{
                    background: 'rgba(255,255,255,0.98)',
                    borderRadius: { xs: 3, md: 4 },
                    p: { xs: 2.5, md: 4 },
                    boxShadow: '0 25px 50px rgba(0,0,0,0.3)',
                    mx: { xs: 1, md: 0 },
                    border: `3px solid ${colors.secondary}`,
                    position: 'relative',
                    overflow: 'visible',
                  }}
                >
                  {/* Badge on card */}
                  <Chip
                    icon={<ElectricBoltIcon sx={{ color: '#fff !important', fontSize: 16 }} />}
                    label={`נותרו ${spotsLeft} מקומות במבצע!`}
                    sx={{
                      position: 'absolute',
                      top: -16,
                      right: 20,
                      background: colors.accent,
                      color: '#fff',
                      fontWeight: 700,
                      py: 2,
                      gap: 0.5,
                    }}
                  />
                  
                  <Typography variant="h5" sx={{ fontWeight: 800, color: colors.dark, mb: 1, textAlign: 'center', fontSize: { xs: '1.2rem', md: '1.5rem' }, mt: 1 }}>
                    קבל הצעת מחיר חינם
                  </Typography>
                  <Typography sx={{ color: colors.gray, mb: { xs: 2, md: 3 }, textAlign: 'center', fontSize: { xs: '0.85rem', md: '1rem' } }}>
                    ללא התחייבות • מענה תוך דקות
                  </Typography>

                  <Box component="form" onSubmit={handleSubmit}>
                    <TextField
                      fullWidth
                      label="שם מלא"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      size="medium"
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      label="טלפון"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      size="medium"
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      label="איזה שירות צריך?"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      multiline
                      rows={2}
                      sx={{ mb: { xs: 2, md: 3 } }}
                    />
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      size="large"
                      startIcon={<WhatsAppIcon />}
                      sx={{
                        background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`,
                        py: { xs: 1.5, md: 2 },
                        fontSize: { xs: '1rem', md: '1.1rem' },
                        fontWeight: 700,
                        borderRadius: 3,
                        gap: 1.5,
                        '&:hover': { transform: 'scale(1.02)', background: colors.accent },
                      }}
                    >
                      שלח ונחזור אליך
                    </Button>
                  </Box>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Trust Bar - רצועת אמון */}
      <Box 
        sx={{ 
          background: colors.dark,
          py: { xs: 2, md: 3 },
          borderTop: `4px solid ${colors.secondary}`,
          borderBottom: `4px solid ${colors.secondary}`,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={2} alignItems="center" justifyContent="center">
            {[
              { icon: '⭐', value: '500+', label: 'לקוחות מרוצים' },
              { icon: '🏆', value: '8', label: 'שנות ניסיון' },
              { icon: '⚡', value: '24/6', label: 'שירות חירום' },
              { icon: '✅', value: '100%', label: 'אחריות' },
            ].map((item, i) => (
              <Grid item xs={6} sm={3} key={i}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: { xs: '1.5rem', md: '2rem' }, mb: 0.5 }}>{item.icon}</Typography>
                    <Typography sx={{ color: colors.gold, fontWeight: 800, fontSize: { xs: '1.5rem', md: '2rem' } }}>
                      {item.value}
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: { xs: '0.75rem', md: '0.9rem' } }}>
                      {item.label}
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Services Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, background: colors.white }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Chip label="השירותים שלנו" sx={{ mb: 2, background: colors.light, color: colors.primary, fontWeight: 600, fontSize: { xs: '0.75rem', md: '0.875rem' } }} />
            <Typography variant="h2" sx={{ fontWeight: 800, color: colors.dark, fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2.5rem' }, mb: 2 }}>
              כל מה שהבית שלך צריך
            </Typography>
            <Typography sx={{ color: colors.gray, maxWidth: 600, mx: 'auto', fontSize: { xs: '0.9rem', md: '1rem' }, px: { xs: 2, md: 0 } }}>
              מגוון רחב של שירותים מקצועיים תחת קורת גג אחת
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 1.5, md: 3 }}>
            {services.map((service, i) => (
              <Grid item xs={6} md={3} key={i}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  whileHover={{ y: -5 }}
                >
                  <Card
                    sx={{
                      p: { xs: 1.5, md: 3 },
                      textAlign: 'center',
                      height: '100%',
                      border: service.highlight ? `2px solid ${colors.secondary}` : '1px solid #E2E8F0',
                      borderRadius: { xs: 2, md: 3 },
                      position: 'relative',
                      overflow: 'visible',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      '&:hover': {
                        boxShadow: '0 15px 30px rgba(0,0,0,0.1)',
                        borderColor: colors.secondary,
                      },
                    }}
                  >
                    {service.highlight && (
                      <Chip
                        label="24/6"
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: -10,
                          right: { xs: 8, md: 12 },
                          background: colors.secondary,
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: { xs: '0.65rem', md: '0.75rem' },
                          height: { xs: 20, md: 24 },
                        }}
                      />
                    )}
                    <Box sx={{ color: colors.primary, mb: { xs: 1, md: 2 }, '& svg': { fontSize: { xs: 28, md: 40 } } }}>{service.icon}</Box>
                    <Typography sx={{ fontWeight: 700, color: colors.dark, mb: { xs: 0.5, md: 1 }, fontSize: { xs: '0.85rem', md: '1rem' } }}>{service.title}</Typography>
                    <Typography sx={{ color: colors.gray, fontSize: { xs: '0.7rem', md: '0.9rem' }, lineHeight: 1.4, display: { xs: 'none', sm: 'block' } }}>{service.desc}</Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Why Us Section */}
      <Box sx={{ py: { xs: 6, md: 12 }, background: colors.light }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, md: 3 } }}>
          <Grid container spacing={{ xs: 3, md: 6 }} alignItems="center">
            <Grid item xs={12} md={5}>
              <Box sx={{ textAlign: { xs: 'center', md: 'right' } }}>
                <Chip label="למה דווקא אנחנו?" sx={{ mb: 2, background: colors.white, color: colors.primary, fontWeight: 600, fontSize: { xs: '0.75rem', md: '0.875rem' } }} />
                <Typography variant="h2" sx={{ fontWeight: 800, color: colors.dark, fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2.5rem' }, mb: { xs: 2, md: 3 } }}>
                  מקצועיות שמדברת
                  <br />
                  <Box component="span" sx={{ color: colors.secondary }}>בעד עצמה</Box>
                </Typography>
                <Typography sx={{ color: colors.gray, mb: { xs: 3, md: 4 }, lineHeight: 1.8, fontSize: { xs: '0.9rem', md: '1rem' } }}>
                  כבר 8 שנים שאנחנו נותנים שירות אמין ומקצועי לאלפי לקוחות מרוצים בכל הארץ.
                  המוניטין שלנו נבנה על עבודה קשה, יושר ועמידה בהבטחות.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={scrollToForm}
                  sx={{
                  background: colors.primary,
                  py: 1.5,
                  px: 4,
                  borderRadius: 3,
                  fontWeight: 700,
                  '&:hover': { background: colors.dark },
                }}
              >
                קבל הצעת מחיר
              </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={7}>
              <Grid container spacing={{ xs: 1.5, md: 2 }}>
                {advantages.map((adv, i) => (
                  <Grid item xs={6} key={i}>
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1 }}
                    >
                      <Card sx={{ p: { xs: 1.5, md: 3 }, borderRadius: { xs: 2, md: 3 }, height: '100%', background: colors.white }}>
                        <Box sx={{ color: colors.secondary, mb: { xs: 0.5, md: 1.5 }, '& svg': { fontSize: { xs: 20, md: 24 } } }}>{adv.icon}</Box>
                        <Typography sx={{ fontWeight: 700, color: colors.dark, mb: 0.5, fontSize: { xs: '0.8rem', md: '1rem' } }}>{adv.title}</Typography>
                        <Typography sx={{ color: colors.gray, fontSize: { xs: '0.7rem', md: '0.9rem' }, lineHeight: 1.4, display: { xs: 'none', sm: 'block' } }}>{adv.desc}</Typography>
                      </Card>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Gallery Section */}
      <Box sx={{ py: { xs: 6, md: 12 }, background: colors.light }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, md: 3 } }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 6 } }}>
            <Chip label="העבודות שלנו" sx={{ mb: 2, background: colors.white, color: colors.primary, fontWeight: 600, fontSize: { xs: '0.75rem', md: '0.875rem' } }} />
            <Typography variant="h2" sx={{ fontWeight: 800, color: colors.dark, fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2.5rem' } }}>
              גלריית פרויקטים
            </Typography>
            <Typography sx={{ color: colors.gray, mt: 1, fontSize: { xs: '0.85rem', md: '1rem' } }}>
              לחץ על קטגוריה כדי לצפות בכל התמונות
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 1.5, md: 3 }}>
            {galleryCategories.map((category, catIndex) => (
              <Grid item xs={6} sm={4} md={4} key={catIndex}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: catIndex * 0.1 }}
                  whileHover={{ scale: 1.03 }}
                >
                  <Box
                    onClick={() => openLightbox(catIndex)}
                    sx={{
                      height: { xs: 160, sm: 200, md: 280 },
                      borderRadius: { xs: 2, md: 3 },
                      position: 'relative',
                      overflow: 'hidden',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                      cursor: 'pointer',
                      '&:hover .overlay': {
                        opacity: 1,
                      },
                      '&:hover img': {
                        transform: 'scale(1.1)',
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={category.cover}
                      alt={category.name}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.5s ease',
                      }}
                    />
                    {/* Overlay */}
                    <Box
                      className="overlay"
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        background: `linear-gradient(to top, ${category.color}ee 0%, ${category.color}88 40%, transparent 100%)`,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                        p: { xs: 1.5, md: 2.5 },
                        opacity: 0.9,
                        transition: 'opacity 0.3s ease',
                      }}
                    >
                      <Chip 
                        label={`${category.images.length} תמונות`} 
                        size="small" 
                        sx={{ 
                          alignSelf: 'flex-start',
                          mb: { xs: 0.5, md: 1 },
                          background: 'rgba(255,255,255,0.2)', 
                          color: '#fff',
                          fontWeight: 600,
                          backdropFilter: 'blur(4px)',
                          fontSize: { xs: '0.65rem', md: '0.75rem' },
                          height: { xs: 20, md: 24 },
                        }} 
                      />
                      <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: { xs: '0.85rem', md: '1.2rem' } }}>
                        {category.name}
                      </Typography>
                      <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: { xs: '0.7rem', md: '0.9rem' }, mt: 0.5, display: { xs: 'none', sm: 'block' } }}>
                        {category.desc}
                      </Typography>
                    </Box>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center', mt: { xs: 3, md: 5 } }}>
            <Button
              variant="contained"
              size="large"
              onClick={scrollToForm}
              sx={{
                background: colors.primary,
                py: 1.5,
                px: 4,
                borderRadius: 3,
                fontWeight: 700,
                '&:hover': { background: colors.dark },
              }}
            >
              רוצה תוצאות כאלה? דבר איתנו
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Testimonials Section - מערכת ביקורות */}
      <Box sx={{ py: { xs: 6, md: 12 }, background: colors.white }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, md: 3 } }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 6 } }}>
            <Chip label="מה אומרים עלינו" sx={{ mb: 2, background: colors.light, color: colors.primary, fontWeight: 600, fontSize: { xs: '0.75rem', md: '0.875rem' } }} />
            <Typography variant="h2" sx={{ fontWeight: 800, color: colors.dark, fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2.5rem' }, mb: 1 }}>
              לקוחות מרוצים
            </Typography>
            <Typography sx={{ color: colors.gray, fontSize: { xs: '0.85rem', md: '1rem' } }}>
              {testimonials.length + reviews.length} ביקורות מאומתות
            </Typography>
          </Box>

          {/* Add Review Button */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowReviewForm(!showReviewForm)}
              sx={{
                background: showReviewForm ? colors.gray : colors.secondary,
                py: 1.5,
                px: 4,
                borderRadius: 3,
                fontWeight: 700,
                '&:hover': { background: showReviewForm ? colors.dark : colors.primary },
              }}
            >
              {showReviewForm ? 'סגור' : 'הוסף ביקורת'}
            </Button>
          </Box>

          {/* Review Form */}
          <AnimatePresence>
            {showReviewForm && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Card sx={{ p: { xs: 2, md: 4 }, mb: 4, maxWidth: 600, mx: 'auto', borderRadius: 3, border: `2px solid ${colors.secondary}` }}>
                  <Typography sx={{ fontWeight: 700, color: colors.dark, mb: 2, textAlign: 'center' }}>
                    ספר/י לנו על החוויה שלך
                  </Typography>
                  <Box component="form" onSubmit={handleReviewSubmit}>
                    <TextField
                      fullWidth
                      label="שם"
                      value={reviewForm.name}
                      onChange={(e) => setReviewForm({ ...reviewForm, name: e.target.value })}
                      required
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      select
                      label="סוג שירות"
                      value={reviewForm.service}
                      onChange={(e) => setReviewForm({ ...reviewForm, service: e.target.value })}
                      SelectProps={{ native: true }}
                      sx={{ mb: 2 }}
                    >
                      {['שיפוצים', 'אינסטלציה', 'מזגנים', 'דודי שמש', 'סורגים', 'בנייה קלה', 'דלתות'].map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </TextField>
                    <Box sx={{ mb: 2 }}>
                      <Typography sx={{ color: colors.gray, mb: 1, fontSize: '0.9rem' }}>דירוג:</Typography>
                      <Rating
                        value={reviewForm.rating}
                        onChange={(e, v) => setReviewForm({ ...reviewForm, rating: v })}
                        size="large"
                      />
                    </Box>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="הביקורת שלך"
                      value={reviewForm.text}
                      onChange={(e) => setReviewForm({ ...reviewForm, text: e.target.value })}
                      required
                      sx={{ mb: 2 }}
                    />
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      endIcon={<SendIcon />}
                      sx={{
                        background: colors.primary,
                        py: 1.5,
                        fontWeight: 700,
                        borderRadius: 2,
                        '&:hover': { background: colors.dark },
                      }}
                    >
                      שלח ביקורת
                    </Button>
                  </Box>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Reviews Grid */}
          <Grid container spacing={{ xs: 2, md: 3 }}>
            {/* New reviews from users */}
            {reviews.map((t, i) => (
              <Grid item xs={12} md={6} key={`new-${t.id}`}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Card sx={{ p: { xs: 2, md: 4 }, borderRadius: { xs: 2, md: 3 }, height: '100%', position: 'relative', border: `2px solid ${colors.gold}` }}>
                    <Chip label="חדש!" size="small" sx={{ position: 'absolute', top: 10, left: 10, background: colors.gold, color: '#fff', fontWeight: 700 }} />
                    <FormatQuoteIcon sx={{ position: 'absolute', top: { xs: 8, md: 16 }, right: { xs: 8, md: 16 }, color: colors.lightGray, fontSize: { xs: 24, md: 40 }, transform: 'scaleX(-1)' }} />
                    <Stack direction="row" spacing={{ xs: 1, md: 2 }} alignItems="center" sx={{ mb: { xs: 1, md: 2 } }}>
                      <Avatar sx={{ width: { xs: 40, md: 56 }, height: { xs: 40, md: 56 }, background: colors.secondary, fontWeight: 700, fontSize: { xs: '0.9rem', md: '1.2rem' } }}>
                        {t.name.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography sx={{ fontWeight: 700, color: colors.dark, fontSize: { xs: '0.85rem', md: '1rem' } }}>{t.name}</Typography>
                        <Typography sx={{ color: colors.gray, fontSize: { xs: '0.75rem', md: '0.85rem' } }}>{t.date}</Typography>
                      </Box>
                      <Chip label={t.service} size="small" sx={{ ml: 'auto', background: colors.light, fontSize: { xs: '0.65rem', md: '0.75rem' } }} />
                    </Stack>
                    <Rating value={t.rating} readOnly size="small" sx={{ mb: { xs: 1, md: 2 } }} />
                    <Typography sx={{ color: colors.gray, lineHeight: 1.7, fontSize: { xs: '0.85rem', md: '1rem' } }}>"{t.text}"</Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}

            {/* Original testimonials */}
            {testimonials.map((t, i) => (
              <Grid item xs={12} md={6} key={`orig-${i}`}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Card sx={{ p: { xs: 2, md: 4 }, borderRadius: { xs: 2, md: 3 }, height: '100%', position: 'relative' }}>
                    <FormatQuoteIcon sx={{ position: 'absolute', top: { xs: 8, md: 16 }, right: { xs: 8, md: 16 }, color: colors.lightGray, fontSize: { xs: 24, md: 40 }, transform: 'scaleX(-1)' }} />
                    <Stack direction="row" spacing={{ xs: 1, md: 2 }} alignItems="center" sx={{ mb: { xs: 1, md: 2 } }}>
                      <Avatar sx={{ width: { xs: 40, md: 56 }, height: { xs: 40, md: 56 }, background: colors.primary, fontWeight: 700, fontSize: { xs: '0.9rem', md: '1.2rem' } }}>
                        {t.name.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography sx={{ fontWeight: 700, color: colors.dark, fontSize: { xs: '0.85rem', md: '1rem' } }}>{t.name}</Typography>
                        <Typography sx={{ color: colors.gray, fontSize: { xs: '0.75rem', md: '0.85rem' } }}>{t.location}</Typography>
                      </Box>
                      <Chip label={t.service} size="small" sx={{ ml: 'auto', background: colors.light, fontSize: { xs: '0.65rem', md: '0.75rem' } }} />
                    </Stack>
                    <Rating value={t.rating} readOnly size="small" sx={{ mb: { xs: 1, md: 2 } }} />
                    <Typography sx={{ color: colors.gray, lineHeight: 1.7, fontSize: { xs: '0.85rem', md: '1rem' } }}>"{t.text}"</Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Review Success Snackbar */}
      <Snackbar
        open={reviewSuccess}
        autoHideDuration={3000}
        onClose={() => setReviewSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ fontWeight: 600 }}>
          ✨ תודה! הביקורת שלך נוספה בהצלחה
        </Alert>
      </Snackbar>

      {/* FAQ Section */}
      <Box sx={{ py: { xs: 6, md: 12 }, background: colors.light }}>
        <Container maxWidth="md" sx={{ px: { xs: 2, md: 3 } }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 6 } }}>
            <Chip label="שאלות נפוצות" sx={{ mb: 2, background: colors.white, color: colors.primary, fontWeight: 600, fontSize: { xs: '0.75rem', md: '0.875rem' } }} />
            <Typography variant="h2" sx={{ fontWeight: 800, color: colors.dark, fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2.5rem' } }}>
              יש שאלות? יש תשובות
            </Typography>
          </Box>

          {faqItems.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
            >
              <Accordion
                sx={{
                  mb: { xs: 1, md: 2 },
                  borderRadius: { xs: '8px !important', md: '12px !important' },
                  overflow: 'hidden',
                  '&:before': { display: 'none' },
                  boxShadow: 'none',
                  border: '1px solid #E2E8F0',
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: { xs: 48, md: 56 } }}>
                  <Typography sx={{ fontWeight: 600, color: colors.dark, fontSize: { xs: '0.85rem', md: '1rem' } }}>{item.q}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Typography sx={{ color: colors.gray, fontSize: { xs: '0.85rem', md: '1rem' }, lineHeight: 1.6 }}>{item.a}</Typography>
                </AccordionDetails>
              </Accordion>
            </motion.div>
          ))}
        </Container>
      </Box>

      {/* Contact Section */}
      <Box
        id="contact-form"
        sx={{
          py: { xs: 6, md: 12 },
          background: `linear-gradient(135deg, ${colors.dark} 0%, ${colors.primary} 100%)`,
        }}
      >
        <Container maxWidth="md" sx={{ px: { xs: 2, md: 3 } }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 6 } }}>
            <Typography variant="h2" sx={{ fontWeight: 800, color: '#fff', fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2.5rem' }, mb: { xs: 1, md: 2 } }}>
              מוכנים להתחיל?
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.8)', maxWidth: 500, mx: 'auto', fontSize: { xs: '0.9rem', md: '1rem' }, px: { xs: 1, md: 0 } }}>
              השאירו פרטים ונחזור אליכם תוך דקות עם הצעת מחיר
            </Typography>
          </Box>

          <Card sx={{ p: { xs: 2.5, md: 5 }, borderRadius: { xs: 3, md: 4 }, maxWidth: 500, mx: 'auto' }}>
            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="שם מלא"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                size="medium"
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="טלפון"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
                size="medium"
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="ספר לנו מה אתה צריך"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                multiline
                rows={3}
                sx={{ mb: { xs: 2, md: 3 } }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                startIcon={<WhatsAppIcon />}
                sx={{
                  background: '#25D366',
                  py: { xs: 1.5, md: 2 },
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  fontWeight: 700,
                  borderRadius: 3,
                  gap: 1.5, // מרחק בין אייקון לטקסט
                  '&:hover': { background: '#20BD5A' },
                }}
              >
                שלח הודעה בוואטסאפ
              </Button>
            </Box>

            <Typography sx={{ textAlign: 'center', mt: { xs: 2, md: 3 }, color: colors.gray, fontSize: { xs: '0.85rem', md: '1rem' } }}>
              או התקשר ישירות:
            </Typography>
            <Button
              fullWidth
              variant="outlined"
              size="large"
              startIcon={<PhoneIcon />}
              onClick={handleCall}
              sx={{ mt: 1, borderRadius: 3, fontWeight: 700, py: { xs: 1.2, md: 1.5 }, fontSize: { xs: '0.95rem', md: '1rem' }, gap: 1.5 }}
            >
              {PHONE_NUMBER}
            </Button>
          </Card>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ py: { xs: 3, md: 4 }, background: colors.dark, textAlign: 'center', px: 2 }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: { xs: '0.8rem', md: '1rem' } }}>
          © 2024 דניאל אחזקות מבנים | כל הזכויות שמורות
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.4)', mt: 1, fontSize: { xs: '0.75rem', md: '0.85rem' } }}>
          א׳-ו׳ 07:00-17:00 | אינסטלציה 24/6 | {PHONE_NUMBER}
        </Typography>
      </Box>

      {/* WhatsApp FAB */}
      <motion.div
        style={{ position: 'fixed', bottom: 16, left: 16, zIndex: 1000 }}
        animate={{ scale: [1, 1.1, 1] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <Button
          variant="contained"
          onClick={() => handleWhatsApp()}
          sx={{
            width: { xs: 56, md: 64 },
            height: { xs: 56, md: 64 },
            borderRadius: '50%',
            minWidth: 0,
            background: '#25D366',
            boxShadow: '0 8px 25px rgba(37, 211, 102, 0.4)',
            '&:hover': { background: '#20BD5A' },
          }}
        >
          <WhatsAppIcon sx={{ fontSize: { xs: 28, md: 32 } }} />
        </Button>
      </motion.div>

      {/* Lightbox Dialog */}
      <Dialog
        open={lightboxOpen}
        onClose={closeLightbox}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(0,0,0,0.95)',
            boxShadow: 'none',
            maxHeight: '95vh',
            m: { xs: 1, md: 4 },
            width: { xs: 'calc(100% - 16px)', md: 'auto' },
          }
        }}
      >
        <Box sx={{ position: 'relative', width: '100%', height: { xs: '70vh', md: '80vh' }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {/* Close Button */}
          <IconButton
            onClick={closeLightbox}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: '#fff',
              background: 'rgba(255,255,255,0.1)',
              '&:hover': { background: 'rgba(255,255,255,0.2)' },
              zIndex: 10,
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Previous Button */}
          {getCurrentCategory()?.images.length > 1 && (
            <IconButton
              onClick={prevImage}
              sx={{
                position: 'absolute',
                left: 16,
                color: '#fff',
                background: 'rgba(255,255,255,0.1)',
                '&:hover': { background: 'rgba(255,255,255,0.2)' },
                zIndex: 10,
              }}
            >
              <ChevronRightIcon sx={{ fontSize: 40 }} />
            </IconButton>
          )}

          {/* Image */}
          <AnimatePresence mode="wait">
            {getCurrentImage() && (
              <motion.img
                key={`${selectedCategoryIndex}-${lightboxIndex}`}
                src={getCurrentImage()?.img}
                alt={getCurrentImage()?.title}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
                style={{
                  maxWidth: '90%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  borderRadius: 8,
                }}
              />
            )}
          </AnimatePresence>

          {/* Next Button */}
          {getCurrentCategory()?.images.length > 1 && (
            <IconButton
              onClick={nextImage}
              sx={{
                position: 'absolute',
                right: 16,
                color: '#fff',
                background: 'rgba(255,255,255,0.1)',
                '&:hover': { background: 'rgba(255,255,255,0.2)' },
                zIndex: 10,
              }}
            >
              <ChevronLeftIcon sx={{ fontSize: 40 }} />
            </IconButton>
          )}

          {/* Image Info */}
          <Box sx={{ position: 'absolute', bottom: 20, textAlign: 'center', width: '100%' }}>
            <Chip 
              label={getCurrentCategory()?.name} 
              sx={{ mb: 1, background: getCurrentCategory()?.color || colors.secondary, color: '#fff', fontWeight: 600 }} 
            />
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.3rem' }}>
              {getCurrentImage()?.title}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', mt: 1, fontSize: '0.85rem' }}>
              {lightboxIndex + 1} / {getCurrentCategory()?.images.length || 0}
            </Typography>
          </Box>
        </Box>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={4000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ fontWeight: 600 }}>
          מעבירים אותך לוואטסאפ...
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DanielLandingPage;
